import apiClient from './client';
import type { AttendanceMarkingStatus, AbsentReason } from './coachPortal';

/**
 * MED-794 / 2.6a — Coach Post-Session screen API client.
 *
 * Mirrors medley-api/services/CoachPostSessionService.ts. Backend Date
 * columns serialize to ISO 8601 strings over the wire.
 *
 * Attendance / engagement / feedback submissions continue to use
 * `coachPortalApi` (markAttendance / submitFeedback) — those endpoints
 * already back the legacy `PostSessionModal` and are reused per AC3 / AC6
 * ("connects to existing MED01 backend").
 */

// ---------------------------------------------------------------------------
// Type mirrors
// ---------------------------------------------------------------------------

export type PostSessionRsvpStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'tentative';

export interface PostSessionParticipant {
  user_id: string;
  first_name: string;
  last_name: string;
  // MED-1162 — preferred name when set; the attendance row appends it as a
  // dimmed "(preferred)" parenthetical (AC5).
  preferred_name: string | null;
  email: string;
  attendance_id: string | null;
  current_status: AttendanceMarkingStatus | 'pending' | 'present' | 'excused' | 'late';
  current_absent_reason: AbsentReason | null;
  current_notes: string | null;
  // MED-979: coach-authored per-participant free-text note. Independent of
  // current_notes (the legacy operator-facing field).
  current_coach_notes: string | null;
  // MED-1191: true when current_status was auto-inferred from the SGOS presence
  // heartbeat at session end and not yet leader-confirmed. Derived server-side
  // (status set + no recorder), so it clears once the leader saves. Drives the
  // AC8 per-row review flag and the AC9 pre-fill header note.
  status_auto_populated: boolean;
  rsvp_status: PostSessionRsvpStatus;
  participant_status?: string;
  withdrawn_at?: string | null;
}

export interface PostSessionEngagementRating {
  id: string;
  session_id: string;
  participant_id: string;
  coach_id: string;
  rating: number;
  created_at: string;
  updated_at: string;
  participant_name: string;
  participant_email: string;
  coach_name: string;
}

export interface PostSessionCoachFeedback {
  id: string;
  session_id: string;
  coach_id: string;
  responses: Record<string, string>;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PostSessionCommitment {
  id: string;
  session_id: string;
  participant_user_id: string;
  /** MED-1216 — NULL for participant-authored rows. */
  coach_user_id: string | null;
  /**
   * MED-1216 — the actual author. Participant-authored ⟺
   * `authored_by_user_id === participant_user_id` (⟺ `coach_user_id === null`),
   * in which case the coach view is read-only ("saved by participant", AC8) and
   * a coach write is rejected server-side (AC4).
   */
  authored_by_user_id: string;
  commitment_text: string;
  created_at: string;
  updated_at: string;
}

export interface PostSessionSessionWithDetails {
  id: string;
  cohort_id: string;
  group_id: string | null;
  participant_id: string | null;
  title: string;
  session_number: number | null;
  session_type: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  status: string;
  group_name: string | null;
  cohort_name: string | null;
  cohort_code: string | null;
  topic_name: string | null;
  coach_session_notes_html: string | null;
  coach_session_notes_plain: string | null;
  coach_session_notes_updated_at: string | null;
  duration_minutes: number | null;
  // MED-867: canonical submitted signal for the Medley Feedback Form. A saved
  // draft creates a coach_session_feedback row (whose own submitted_at defaults
  // to now()), so "is this submitted?" must read the session flag — NOT the
  // feedback row's submitted_at — or a draft would look submitted.
  feedback_submitted: boolean;
  feedback_submitted_at: string | null;
  /**
   * MED-1181 — the cohort's group-coaching per-modality Live Session Delivery
   * gate (via `Session.live_session_delivery_enabled` on the underlying
   * `SessionWithDetails`); `false` for a classic MED01 cohort. Used as the
   * page-level LSD gate (defense-in-depth) so the coach post-session shell
   * fails closed.
   */
  live_session_delivery_enabled: boolean;
}

export interface PostSessionBundle {
  session: PostSessionSessionWithDetails;
  participants: PostSessionParticipant[];
  engagement_ratings: PostSessionEngagementRating[];
  feedback: PostSessionCoachFeedback | null;
  commitments: PostSessionCommitment[];
  /**
   * MED-971 AC1 — true iff the cohort's group_coaching modality has
   * `ai_features_enabled=true`. Drives the Send Recap Email modal's body seed:
   * false → default template; true → AI envelope, with template fallback.
   */
  ai_features_enabled: boolean;
  /**
   * MED-1128 — session-level AI listening (`sessions.ai_listening_enabled`).
   * Post-session AI (recap) needs BOTH this AND `ai_features_enabled`: a session
   * can have AI available at the modality level but off for the session, in which
   * case no transcript exists and no AI recap is produced. When this is false,
   * the recap uses the plain template (same as modality-off) plus an inline note.
   */
  ai_listening_enabled: boolean;
  /**
   * MED-971 AC2 — ISO 8601 scheduled_start of the next group session for the
   * cohort (session_number + 1). Null when no future session is scheduled.
   * Resolves the `{{next_session_date}}` merge tag.
   */
  next_session_date: string | null;
  /**
   * MED-971 AC1/AC7 — admin-managed default body HTML for the modal. Null when
   * no template is configured; body stays empty in that case.
   */
  recap_email_default_template: string | null;
  /**
   * MED-894 (B7) — aggregate airtime split for the Airtime Balance card.
   * Null when AI wasn't active for the session (card absent, AC7).
   */
  airtime: AirtimeEnvelope | null;
  /**
   * MED-1187 (AC8) — a persisted session transcript row exists. Drives the
   * post-session "Delete transcript" control (hidden when false).
   */
  has_transcript: boolean;
  /** MED-1193 (B9) — session themes, COACH register only (server-scoped). */
  themes: SessionThemesApiEnvelope | null;
  /**
   * MED-1194 (B10) — In-session insights (fixed airtime-tier copy, selected
   * server-side off the stored reading). Coach view may carry the
   * one-dominating message alongside the aggregate tier. Null when AI wasn't
   * active (card absent).
   */
  insession_insights: InSessionInsightsApiEnvelope | null;
  /**
   * MED-1213 — the session leader's durable recap-email draft edit, persisted
   * server-side (Case 2). Null when the leader has not edited; otherwise seeds
   * the Recap Email Draft card so edits survive a tab/browser close or a
   * different device. Empty fields → the AI/template seed still runs.
   */
  recap_draft_edit: RecapDraftEdit | null;
}

/** MED-1213 — persisted recap-email draft edit (both fields null until edited). */
export interface RecapDraftEdit {
  subject: string | null;
  body_html: string | null;
}

/** MED-1194 (B10) — view-scoped insights envelope (server sends this view's messages only). */
export interface InSessionInsightsApiEnvelope {
  status: 'available' | 'not_available';
  messages?: Array<{
    kind: 'airtime_split' | 'airtime_dominating';
    title: string;
    text: string;
  }>;
}

/** MED-1193 (B9) — register-scoped themes envelope (the server sends one register). */
export interface SessionThemesApiEnvelope {
  status: 'available' | 'pending' | 'not_available';
  themes?: Array<{ title: string; description: string }>;
}

/**
 * MED-894 (B7) — the aggregate airtime envelope computed server-side from the
 * persisted diarized transcript (the shared airtime foundation). The card
 * renders: available → split bar; pending → loading; not_available → honest
 * "not available" line. Never compute shares client-side.
 */
export interface AirtimeEnvelope {
  status: 'available' | 'pending' | 'not_available';
  /**
   * Why airtime is unavailable (observability only; the card renders a single
   * generic line). Present only on not_available. (MED-1212 dropped
   * `retention_disabled` — the retention flag no longer exists.)
   */
  reason?: 'no_transcript' | 'insufficient_transcript';
  split?: {
    leader_kind: 'coach' | 'peer_leader';
    leader_share_pct: number;
    participants_share_pct: number;
    leader_seconds: number;
    participants_seconds: number;
  };
  /**
   * MED-1192 (B6) — anonymized per-participant shares of the PARTICIPANT
   * bucket (roster-ordered stable labels). Coach bundle only; the peer-leader
   * bundle never carries it (leader view = split bar alone).
   */
  per_participant?: Array<{
    label: string;
    seconds: number;
    share_of_participants_pct: number;
  }>;
}

export interface UpsertCommitmentInput {
  participant_user_id: string;
  commitment_text: string;
}

export interface UpsertCommitmentsRequest {
  commitments: UpsertCommitmentInput[];
}

export interface UpsertCommitmentsResponse {
  commitments: PostSessionCommitment[];
}

// MED-901 / E2.16 — coach recap email send.
export interface SendRecapEmailRequest {
  recipient_user_ids: string[];
  /** Coach-authored subject (merge tags allowed — resolved server-side per recipient) */
  subject: string;
  /** Coach-authored body HTML from RichTextEditor (merge tags allowed) */
  body_html: string;
}

export interface SendRecapEmailResponse {
  sent: number;
  failed: number;
  skipped_not_in_session: number;
}

// MED-1213 — durable recap-email draft edit (idempotent upsert).
export interface SaveRecapDraftEditRequest {
  subject: string;
  body_html: string;
}

export interface SaveRecapDraftEditResponse {
  subject: string;
  body_html: string;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

/**
 * MED-1187 (AC8) — hard-delete the session's persisted transcript. Idempotent
 * server-side; ownership-guarded (group coach or the designated session leader).
 */
export const deleteSessionTranscript = (sessionId: string) =>
  apiClient.delete<{ deleted: boolean; session_id: string }>(
    `/api/ai/session-transcript/${sessionId}`,
  );

export const coachPostSessionApi = {
  /** GET composite bundle for the post-session screen. */
  getBundle: (sessionId: string): Promise<PostSessionBundle> =>
    apiClient.get<PostSessionBundle>(`/api/coach/sessions/${sessionId}/post-session`),

  /** PUT bulk upsert of per-participant Log Commitments. */
  upsertCommitments: (
    sessionId: string,
    body: UpsertCommitmentsRequest,
  ): Promise<UpsertCommitmentsResponse> =>
    apiClient.put<UpsertCommitmentsResponse>(
      `/api/coach/sessions/${sessionId}/post-session/commitments`,
      body,
    ),

  /** POST send the coach-authored recap email (MED-901 / E2.16). */
  sendRecapEmail: (
    sessionId: string,
    body: SendRecapEmailRequest,
  ): Promise<SendRecapEmailResponse> =>
    apiClient.post<SendRecapEmailResponse>(
      `/api/coach/sessions/${sessionId}/post-session/recap-email/send`,
      body,
    ),

  /** PUT idempotent upsert of the leader's durable recap draft edit (MED-1213). */
  saveRecapDraftEdit: (
    sessionId: string,
    body: SaveRecapDraftEditRequest,
  ): Promise<SaveRecapDraftEditResponse> =>
    apiClient.put<SaveRecapDraftEditResponse>(
      `/api/coach/sessions/${sessionId}/post-session/recap-email/draft`,
      body,
    ),
};
